import {
    Button,
    Card,
    Col,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    Radio,
    Row,
    Select,
    Spin,
    Switch,
    message,
    Breadcrumb,
    theme,
} from "antd";
import { useEffect, useState } from "react";
import { post, get } from "@/common/services/httpClient";
import { useForm } from "antd/es/form/Form";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useAppMutation } from "@/common/hooks/useAppMutation";
import dayjs from "dayjs";
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd";
import { useTranslation } from "react-i18next";
import {
    useBankDetailDropdown,
    useMedicalProviderDropdown,
    usePlanServiceDropdown,
    usePatientDropdown,
    type DropdownOption
} from "@/common/components/DynamicSelect";
import AddServiceItemModal from "./AddServiceItemModal";
import AddOtherServiceItemModal from "./AddOtherServiceItemModal";
import BankDetailsModal from "./BankDetailsModal";
import { API_ENDPOINTS } from '../../api/apiEndpoints';
import FileUploadRequestField from "../../components/FileUploadRequestField";
import type { PolicyHolder, PolicyHolderResponseItem } from "../../../participants/types/participantsTypes";
import { useAuthStore } from "../../../auth/store/authStore";
import { currencyAmountValidator } from "../../../../common/components/MultipleOfValidator";
import Tooltip from "antd/lib/tooltip";
import { useUnsavedChangesStore } from '@/app/store/unsavedChangesStore';
import { useNavigationBlocker } from '@/common/hooks/useNavigationBlocker';
import { useUnsavedChangesWarning } from '@/common/hooks/useUnsavedChangesWarning';
interface Service {
    serviceCategoryItemId: number;
    serviceName: string;
    serviceCategoryItemName?: string;
    serviceCategoryId?: number;
    serviceCategoryName?: string;
    //serviceCategoryId: number;          
    //serviceCategoryName: string; 
    amount: number;
    coShare?: number | null;
    coveredAmount?: number | null;
    isAmountCoverage?: boolean | null;
    isServiceCovered?: boolean;
    selected?: any;
}

interface ClaimFormValues {
    policyHolderId: number | undefined;
    claimDate: dayjs.Dayjs;
    patientId: number;
    patientName: string;
    medicalProviderId: number;
    medicalProviderName?: string;
    consultantName: string;
    visitDate: dayjs.Dayjs;
    consultantFee?: number;
    medicineAmount?: number;
    labTestAmount?: number;
    totalAmount?: number;
    referenceNo?: string;
    paymentMethod: string;
    bankDetailId?: number;
    comment?: string;
    files: UploadFile[];
    services?: Service[];
}

interface ClaimPayload extends Omit<ClaimFormValues, "claimDate" | "visitDate" | "files" | "policyHolderId" | "patientName" | "medicalProviderName"> {
    claimDate: string;
    visitDate: string;
    status: string;
    policyCode?: string;
    planId?: number;
    planName?: string;
    customerId?: number;
    customerName?: string;
    companyId?: number;
    companyName?: string;
    policyHolderId?: number;
    cardNumber?: string;
    policyHolderName?: string;
    currencyISOCode?: string;
    files: { name: string }[];
    medicalProviderName?: string;
    patientName?: string | undefined;
    isInNetwork?: boolean;
    id?: number;
    auditHistory?: any[];
}

interface ClaimResponseData {
    id: number;
    policyHolderId: number;
    claimDate: string;
    patientId: number;
    patientName: string;
    medicalProviderId: number;
    medicalProviderName?: string;
    consultantName: string;
    visitDate: string;
    consultantFee?: number;
    medicineAmount?: number;
    labTestAmount?: number;
    totalAmount?: number;
    referenceNo?: string;
    paymentMethod: string;
    bankDetailId?: number;
    comment?: string;
    files?: { name: string }[];
    services?: Service[];
    policyCode?: string;
    planId?: number;
    planName?: string;
    customerId?: number;
    customerName?: string;
    companyId?: number;
    companyName?: string;
    cardNumber?: string;
    policyHolderName?: string;
    currencyISOCode?: string;
    status: string;
    auditHistory?: any[];
}

const ClaimRequestsForm = () => {
    const { token } = theme.useToken();
    const [form] = useForm();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { isDirty, setIsDirty } = useUnsavedChangesStore();
    
    // Use navigation blocker hook to prevent navigation without saving
    useNavigationBlocker(isDirty);
    
    // Use browser warning for page refresh/close
    useUnsavedChangesWarning();
    
    const [paymentMethod, setPaymentMethod] = useState("Cash");
    const [isAddServiceModalVisible, setIsAddServiceModalVisible] = useState(false);
    const [isAddOtherServiceModalVisible, setIsAddOtherServiceModalVisible] = useState(false);
    const [isBankModalVisible, setIsBankModalVisible] = useState(false);
    const policyHolderId = Form.useWatch("policyHolderId", form);
    const currentServices = Form.useWatch('services', form) || [];
    const medicalProviderId = Form.useWatch("medicalProviderId", form);
    const [policyHolderOptions, setPolicyHolderOptions] = useState<DropdownOption[]>([]);
    const [isFileUploading, setIsFileUploading] = useState(false);
    //const [services, setServices] = useState<Service[]>([]);


    const isBabyBorn = Form.useWatch("isBabyBorn", form);

    // const babyFlag = isBabyBorn ? 1 : 0;

    const [isInNetwork, setIsInNetwork] = useState(true);


    const [selectedPolicyHolder, setSelectedPolicyHolder] = useState<DropdownOption | null>(null);

    //console.log('selectedPolicyHolder ', selectedPolicyHolder?.planId);
    const planId = selectedPolicyHolder?.planId;

    const { data: medicalProvidersRaw, isLoading: loadingMedicalProviders } = useMedicalProviderDropdown(isInNetwork);
    const medicalProviders = Array.isArray(medicalProvidersRaw) ? medicalProvidersRaw : [];

    // const { data: serviceItemsRaw, isLoading: loadingServiceItems } = useServiceItemDropdownDependency(medicalProviderId, selectedPolicyHolder?.planId, isInNetwork, selectedPolicyHolder?.value as number | undefined, selectedPolicyHolder?.customerId);
    const { data: bankDetails, isLoading: loadingBankDetails, refetch: refetchBankDetails } = useBankDetailDropdown(policyHolderId);
    const { data: patients, isLoading: loadingPatients } = usePatientDropdown(policyHolderId);

    const { data: serviceItemsRaw, isLoading: loadingServiceItems } = usePlanServiceDropdown(planId, policyHolderId);
    const serviceItems = Array.isArray(serviceItemsRaw) ? serviceItemsRaw : [];

    const [serviceTreeData, setServiceTreeData] = useState<any[]>([]);
    useEffect(() => {
        const fetchServices = async () => {
            if (!planId || !policyHolderId) {
                setServiceTreeData([]);
                return;
            }
            try {
                const res = await get<any>(`Plan/GetServices?planId=${planId}&policyHolderId=${policyHolderId}`);
                const data = res.data || [];
                const tree = Array.isArray(data)
                    ? data.map((cat: any) => ({
                          title: cat.serviceCategoryName,
                          key: `cat-${cat.serviceCategoryId}`,
                          value: `cat-${cat.serviceCategoryId}`,
                          children: Array.isArray(cat.services)
                              ? cat.services.map((s: any) => ({
                                    title: s.serviceCategoryItemName,
                                    key: String(s.serviceCategoryItemId),
                                    value: String(s.serviceCategoryItemId),
                                    isLeaf: true,
                                }))
                              : [],
                      }))
                    : [];
                setServiceTreeData(tree);
            } catch (err) {
                console.error('fetchServices tree failed', err);
                setServiceTreeData([]);
            }
        };
        fetchServices();
    }, [planId, policyHolderId]);

    // when available serviceItems or tree data arrive, fill missing serviceName for existing form entries
    useEffect(() => {
        if (serviceItems.length > 0 || serviceTreeData.length > 0) {
            const services: any[] = form.getFieldValue('services') || [];
            let changed = false;
            const updated = services.map((s) => {
                let out = { ...s };
                if (s.serviceCategoryItemId) {
                    const idStr = String(s.serviceCategoryItemId);
                    // if claim data already provided category item name, use it
                    if (s.serviceCategoryItemName) {
                        changed = true;
                        out.serviceName = s.serviceCategoryItemName;
                        out.selected = { ...(s.selected || {}), label: s.serviceCategoryItemName };
                    }
                    // also fill category name if missing
                    if (!out.serviceCategoryName) {
                        // try to derive from tree or items
                        for (const cat of serviceTreeData) {
                            const c = (cat.children || []).find((c: any) => String(c.value) === idStr || String(c.key) === idStr);
                            if (c) {
                                out.serviceCategoryName = cat.title || cat.label;
                                changed = true;
                                break;
                            }
                        }
                        if (!out.serviceCategoryName) {
                            for (const cat of (serviceItems as any[])) {
                                if (Array.isArray(cat.services)) {
                                    const s2 = cat.services.find((x: any) => String(x.serviceCategoryItemId ?? x.value) === idStr);
                                    if (s2) {
                                        out.serviceCategoryName = cat.serviceCategoryName || cat.label;
                                        changed = true;
                                        break;
                                    }
                                }
                            }
                        }
                    }
                    if ((!out.serviceName || out.serviceName === '') && idStr) {
                        let f = serviceItems.find((it: any) => String(it.value) === idStr);
                        if (!f) {
                            for (const cat of serviceItems as any[]) {
                                if (Array.isArray(cat.services)) {
                                    const ss = cat.services.find((x: any) => String(x.serviceCategoryItemId ?? x.value) === idStr);
                                    if (ss) {
                                        f = ss;
                                        break;
                                    }
                                }
                            }
                        }
                        if (!f && Array.isArray(serviceTreeData)) {
                            for (const cat of serviceTreeData) {
                                const c = (cat.children || []).find((c: any) => String(c.value) === idStr || String(c.key) === idStr);
                                if (c) {
                                    f = { label: c.title || c.label } as any;
                                    break;
                                }
                            }
                        }
                        if (f && f.label) {
                            out.serviceName = f.label;
                            out.selected = { ...(out.selected || {}), label: f.label };
                            changed = true;
                        }
                    }
                }
                return out;
            });
            if (changed) {
                form.setFieldsValue({ services: updated });
            }
        }
    }, [serviceItems, serviceTreeData, form]);


    const userType = useAuthStore((state) => state.userType);

    const { state } = useRouter().state.location;
    const record = (state as { record?: Record<string, unknown> })?.record;
    const id = record?.id?.toString();
    const isEdit = id !== undefined && id !== null; // Derive isEdit based on id presence

    const [initialValues, setInitialValues] = useState<ClaimFormValues | any>(null);
    const [loadingClaim, setLoadingClaim] = useState(false);
    const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL;



    useEffect(() => {
        // When user toggles isBabyBorn
        if (isBabyBorn !== undefined) {
            // Reset policyHolder related fields
            form.setFieldsValue({
                policyHolderId: undefined,
                patientId: undefined,
                bankDetailId: undefined,
                services: [],
                companyName: undefined,
            });

            // Clear selected state
            setSelectedPolicyHolder(null);
            setPolicyHolderOptions([]);
        }
    }, [isBabyBorn, form]);


    // NOTE: companyName is set directly when a policy holder is selected


    useEffect(() => {
        const fetchClaim = async () => {
            if (isEdit && id) {
                setLoadingClaim(true);
                try {
                    const response = await get<any | ClaimResponseData>(`${API_ENDPOINTS.CLAIM.GET_BY_ID}/${id}`);
                    const claimData = response.data; // Corrected: response.data to response
                    // Format dates using dayjs before setting initial values

                    // console.log('claimData.isInNetwork', claimData.isInNetwork);
                    setIsInNetwork(claimData.isInNetwork);

                    setInitialValues({
                        ...claimData,
                        claimDate: dayjs(claimData.claimDate),
                        visitDate: dayjs(claimData.visitDate),
                        files: claimData.files?.map((file: { name: string, id: number }, index: number) => ({
                            uid: `-${index + 1}`,
                            name: file.name.substring(file.name.lastIndexOf('/') + 1),
                            id: file.id,
                            status: 'done',
                            url: `${VITE_API_BASE_URL.replace(/api$/, '')}${file.name}`,
                        })) || [],
                        paymentMethod: claimData.bankDetailId === 0 || !claimData.bankDetailId ? t("Cash") : t("Bank"),
                        services: claimData.services || [],

                    });



                    if (claimData.bankDetailId === 0 || !claimData.bankDetailId) {
                        setPaymentMethod("Cash");
                    } else {
                        setPaymentMethod("Bank");
                    }

                    if (claimData.bankDetailId) {
                        setPaymentMethod("Bank");
                    }

                    //  Initialize selectedPolicyHolder for edit
                    if (claimData.policyHolderId) {
                        setSelectedPolicyHolder({
                            value: claimData.policyHolderId,
                            label: `${claimData.policyHolderName} - ${claimData.cardNumber}`,
                            policyHolderName: claimData.policyHolderName,
                            cardNumber: claimData.cardNumber,
                            planId: claimData.planId,
                            planName: claimData.planName,
                            policyCode: claimData.policyCode,
                            companyId: claimData.companyId,
                            companyName: claimData.companyName,
                            customerId: claimData.customerId,
                            customerName: claimData.customerName,
                            currencyISOCode: claimData.currencyISOCode,
                        });
                    }


                } catch (error) {
                    message.error(t("Failed to fetch claim data"));
                } finally {
                    setLoadingClaim(false);
                }
            }
        };

        fetchClaim();
    }, [isEdit, id]);

    useEffect(() => {
        if (isInNetwork) {
            form.setFieldsValue({
                medicalProviderId: undefined,
                medicalProviderName: undefined,
            });
            setLastSelectedProvider(null);
            return;
        }

        form.setFieldsValue({ medicalProviderId: 0 });
        setLastSelectedProvider(null);
    }, [form, isInNetwork]);

    const handleSwitchChange = (checked: boolean) => {
        setIsInNetwork(checked);
    };

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue(initialValues);
        }
    }, [initialValues, form]);

    useEffect(() => {
        if (patients && patients.length > 0) {
            form.setFieldsValue({ patientId: patients[0].value });
        }
    }, [patients, form]);

    useEffect(() => {
        if (paymentMethod === "Bank" && bankDetails && bankDetails.length === 1) {
            form.setFieldsValue({ bankDetailId: bankDetails[0].value });
        }
    }, [bankDetails, form, paymentMethod]);


    const mutation = useAppMutation(
        (values: ClaimPayload) => {
            return post(isEdit ? "/Claim/Update" : "/Claim/Save", values);
        },
        {
            onSuccess: (response: any) => {
                //console.log("FULL API RESPONSE:", response);

                // axios returns: response.data = actual payload
                // const api = response?.data;

                //console.log("API ERROR:", response);

                if (response?.isError) {
                    message.error(response?.message || response?.title || t("Something went wrong"));
                    return;
                }
                else {
                    message.success(response?.message || t(`Claim ${isEdit ? "updated" : "submitted"} successfully!`));
                }
                setIsDirty(false); // Reset dirty flag on successful submission
                form.resetFields();
                setTimeout(() => navigate({ to: "..", skipBlock: true } as never), 0);
            },

            onError: (error: any) => {
                message.error(error?.response?.data?.message || error.message);
            },
        }
    );

    const handleAddService = (newService: Service) => {
        //console.log('services', newService);
        //setServices(prev => [...prev, newService]);
        const currentServices = form.getFieldValue('services') || [];
        const updatedServices = [...currentServices, newService];
        form.setFieldsValue({ services: updatedServices });

        const { consultantFee = 0, medicineAmount = 0, labTestAmount = 0 } = form.getFieldsValue();
        const servicesTotal = updatedServices.reduce((total, service) => total + (service?.amount || 0), 0);
        const totalAmount = (consultantFee || 0) + (medicineAmount || 0) + (labTestAmount || 0) + servicesTotal;
        form.setFieldsValue({ totalAmount });
    };

    const handleValuesChange = (changedValues: Partial<ClaimFormValues>, allValues: ClaimFormValues) => {
        // Mark form as dirty when any value changes
        setIsDirty(true);
        
        if (
            "consultantFee" in changedValues ||
            "medicineAmount" in changedValues ||
            "labTestAmount" in changedValues ||
            "services" in changedValues
        ) {
            const {
                consultantFee = 0,
                medicineAmount = 0,
                labTestAmount = 0,
                services = [],
            } = allValues;
            const servicesTotal = services?.reduce((total, service) => total + (service?.amount || 0), 0) || 0;
            const totalAmount =
                (consultantFee || 0) + (medicineAmount || 0) + (labTestAmount || 0) + servicesTotal;
            form.setFieldsValue({ totalAmount });
        }

        if ("paymentMethod" in changedValues) {
            setPaymentMethod(changedValues.paymentMethod!);
            form.setFieldsValue({ bankDetailId: undefined });
        }
    };

    const [lastSelectedProvider, setLastSelectedProvider] = useState<string | number | null>(
        initialValues?.medicalProviderId ?? null
    );

    const [lastSelectedPolicyHolder, setLastSelectedPolicyHolder] = useState<string | number | null>(
        initialValues?.policyHolderId ?? null
    );


    const handlePolicyHolderChange = (newPolicyHolder: string | number) => {

        // MUST HAVE → set selected object
        const selected = policyHolderOptions.find(x => x.value === newPolicyHolder) || null;
        setSelectedPolicyHolder(selected);

        const previousPolicyHolder = lastSelectedPolicyHolder;

        // ✅ First time (no previous selection)
        if (!previousPolicyHolder) {
            setLastSelectedPolicyHolder(newPolicyHolder);
            form.setFieldValue("policyHolderId", newPolicyHolder);
            return;
        }

        // ✅ Same value — do nothing
        if (previousPolicyHolder === newPolicyHolder) {
            return;
        }

        //console.log('previousValue', previousPolicyHolder, ' newPolicyHolder', newPolicyHolder);
        // ✅ First time select (or same value) — no confirmation
        if (!previousPolicyHolder || previousPolicyHolder === newPolicyHolder) {
            form.setFieldValue("policyHolderId", newPolicyHolder);
            return;
        }

        // Otherwise, confirm before changing
        // form.setFieldsValue({ bankDetailId: undefined });

        //console.log('services', currentServices);

        if (currentServices.length > 0) {
            Modal.confirm({
                title: t("Confirm Change"),
                content: t("Are you sure you want to clear it's services?"),
                okText: t("Yes"),
                cancelText: t("No"),
                onOk: () => {
                    setLastSelectedPolicyHolder(newPolicyHolder);
                    form.setFieldValue("medicalProviderId", undefined);
                    form.setFieldsValue({ services: [] });
                    handleAddService({ serviceCategoryItemId: 0, serviceName: '',  amount: 0 });
                    form.setFieldsValue({ services: [] });
                },
                onCancel: () => {
                    form.setFieldValue("policyHolderId", previousPolicyHolder);
                    // do nothing, keep old selection
                },
            });
        }

    }

    const handleProviderChange = (newValue: string | number) => {
        // If no previous selection, just set directly
        const previousValue = lastSelectedProvider;

        // ✅ First time (no previous selection)
        if (!previousValue) {
            setLastSelectedProvider(newValue);
            form.setFieldValue("medicalProviderId", newValue);
            return;
        }

        // ✅ Same value — do nothing
        if (previousValue === newValue) {
            return;
        }

        // console.log('previousValue', previousValue);
        // ✅ First time select (or same value) — no confirmation
        if (!previousValue || previousValue === newValue) {
            form.setFieldValue("medicalProviderId", newValue);
            return;
        }

        // Otherwise, confirm before changing
        if (currentServices.length > 0) {
            Modal.confirm({
                title: t("Confirm Change"),
                content: t("Are you sure you want to clear it's services?"),
                okText: t("Yes"),
                cancelText: t("No"),
                onOk: () => {
                    setLastSelectedProvider(newValue);
                    form.setFieldValue("medicalProviderId", newValue);
                    form.setFieldsValue({ services: [] });
                    handleAddService({ serviceCategoryItemId: 0, serviceName: '', amount: 0 });
                    form.setFieldsValue({ services: [] });
                    // message.success("Dependencies cleared successfully.");
                    // clearDependencies(); // <-- call your logic here
                },
                onCancel: () => {
                    form.setFieldValue("medicalProviderId", previousValue);
                    // do nothing, keep old selection
                },
            });
        }
    };

    const onFinish = (values: ClaimFormValues) => {

        if (!values.services || values.services.length === 0) {
            message.error(t("Service is mandatory, select at-least one service"));

            document.getElementById("serviceButton")?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });

            return;
        }

        if (!values.files || values.files.length === 0) {
            message.error(t("Doctor prescription must be attached"));
            document.getElementById("attachmentsCard")?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
            return;
        }

        //const files = values.files?.map(file => ({ name: file.response?.data?.path || file.url })).filter(Boolean) as { name: string }[] || [];
        const files = values.files
            ?.map(file => {
                const fullPath = file.response?.data?.path || file.url;

                // remove base URL if present
                const relativePath = fullPath?.replace(
                    `${VITE_API_BASE_URL.replace(/api$/, '')}`,
                    ''
                );

                return { name: relativePath };
            })
            .filter(Boolean) as { name: string }[] || [];
        const selectedMedicalProvider = medicalProviders.find(
            (p: any) => p.value === values.medicalProviderId
        );
        const enteredMedicalProviderName = values.medicalProviderName?.trim();
        const selectedPatient = patients?.find(
            (p:any) => p.value === values.patientId
        );

        let changedValues: {
            fieldName: string;
            oldValue: string;
            newValue: string;
        }[] = [];

        if (isEdit && initialValues) {
            // const oldValues = initialValues || {};

            changedValues = Object.entries(values)
                .filter(([key, newValue]) => {
                    const oldValue = initialValues[key];
                    const normalize = (v: any) =>
                        dayjs.isDayjs(v) ? v.format("DD-MM-YYYY") : v;
                    return JSON.stringify(normalize(oldValue)) !== JSON.stringify(normalize(newValue));
                })
                .map(([key, newValue]) => {
                    const oldValue = initialValues[key];

                    const normalizeDisplay = (v: any) => {
                        if (v == null) return "";
                        if (dayjs.isDayjs(v)) return v.format("DD-MM-YYYY");
                        if (typeof v === "object") return JSON.stringify(v);
                        return String(v);
                    };

                    return {
                        fieldName: key,
                        oldValue: normalizeDisplay(oldValue),
                        newValue: normalizeDisplay(newValue),
                    };
                });
        }

        const { ...restValues } = values;

        const normalizedServices: Service[] = (values.services || []).map((service) => ({
            ...service,
            serviceCategoryItemId: Number(service.serviceCategoryItemId ?? 0),
            serviceCategoryItemName: service.serviceCategoryItemName || service.serviceName,
            serviceName: service.serviceName || service.serviceCategoryItemName || "",
            coveredAmount: service.coveredAmount ?? null,
            coShare: service.coShare ?? null,
            isAmountCoverage: service.isAmountCoverage ?? false,
            isServiceCovered: service.isServiceCovered ?? false,
        }));

        const payload: ClaimPayload = {
            ...restValues,
            services: normalizedServices,
            claimDate: dayjs(values.claimDate).format("YYYY-MM-DD"),
            visitDate: dayjs(values.visitDate).format("YYYY-MM-DD"),
            status: "Created",
            policyCode: selectedPolicyHolder?.policyCode as string | undefined,
            planId: selectedPolicyHolder?.planId as number | undefined,
            planName: selectedPolicyHolder?.planName as string | undefined,
            policyHolderId: selectedPolicyHolder?.value as number | undefined,
            cardNumber: selectedPolicyHolder?.cardNumber as string | undefined,
            customerId: selectedPolicyHolder?.customerId as number | undefined,
            customerName: selectedPolicyHolder?.customerName as string | undefined,
            companyId: selectedPolicyHolder?.companyId as number | undefined,
            companyName: selectedPolicyHolder?.companyName as string | undefined,
            policyHolderName: selectedPolicyHolder?.policyHolderName as string | undefined,
            currencyISOCode: selectedPolicyHolder?.currencyISOCode as string | undefined,
            files,
            medicalProviderId: isInNetwork ? values.medicalProviderId : 0,
            medicalProviderName: isInNetwork ? selectedMedicalProvider?.label : enteredMedicalProviderName,
            patientName: selectedPatient?.label,
            isInNetwork: isInNetwork,
            auditHistory: changedValues,
            id: isEdit ? Number(id) : undefined,
        };
        // console.log("Payload:", payload);


        mutation.mutate(payload);
    };

    const handleBankModalSuccess = () => {
        setIsBankModalVisible(false);
        refetchBankDetails();
    };

    const allServicesAdded = (() => {
        const availableServiceIds = new Set<string>();

        if (Array.isArray(serviceTreeData) && serviceTreeData.length > 0) {
            for (const category of serviceTreeData) {
                for (const child of (category.children || [])) {
                    const id = String(child.value ?? child.key ?? "");
                    if (id && id !== "0") {
                        availableServiceIds.add(id);
                    }
                }
            }
        }

        if (availableServiceIds.size === 0 && Array.isArray(serviceItems)) {
            for (const item of serviceItems as any[]) {
                if (Array.isArray(item.services)) {
                    for (const service of item.services) {
                        const id = String(service.serviceCategoryItemId ?? service.value ?? "");
                        if (id && id !== "0") {
                            availableServiceIds.add(id);
                        }
                    }
                } else {
                    const id = String(item.value ?? item.serviceCategoryItemId ?? "");
                    if (id && id !== "0") {
                        availableServiceIds.add(id);
                    }
                }
            }
        }

        const addedServiceIds = new Set(
            (currentServices || [])
                .map((service) => String(service?.serviceCategoryItemId ?? ""))
                .filter((id) => id !== "" && id !== "0")
        );

        return availableServiceIds.size > 0 && addedServiceIds.size >= availableServiceIds.size;
    })();

    const handleTrim = (e: React.FocusEvent<HTMLInputElement>) => {
        const cleaned = e.target.value.trim().replace(/\s+/g, " ");
        form.setFieldsValue({ consultantName: cleaned }); // updates the form value
    };

    const amountValidator = (fieldName: string) => ({
        validator: (_: any, value: any) => {
            if (value === undefined || value === null || value === "") return Promise.resolve();
            const num = Number(value);
            if (isNaN(num) || num < 0 || num > 9999999999.99 || !/^\d{1,10}(\.\d{1,2})?$/.test(value.toString())) {
                return Promise.reject(t('validation.amountInvalid', { fieldName }));
            }
            return Promise.resolve();
        },
    });

    // Handle go back navigation
    const handleGoBack = () => {
        setIsDirty(false);
        navigate({ to: '..', skipBlock: true } as never);
    };
    
    // Generate breadcrumb items
    const breadcrumbItems = [
        { label: t('Claims', { defaultValue: 'Claims' }), href: '/claim/requests' },
        { label: isEdit ? t('Edit Claim', { defaultValue: 'Edit Claim' }) : t('Create Claim', { defaultValue: 'Create Claim' }) }
    ];

    return (
        <>
            {/* Breadcrumb and Go Back button in one row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <Breadcrumb>
                    {breadcrumbItems.map((item, idx) =>
                        item.href ? (
                            <Breadcrumb.Item key={idx}>
                                <a href={item.href} onClick={(e) => {
                                    e.preventDefault();
                                    setIsDirty(false);
                                    navigate({ to: item.href });
                                }}>
                                    {item.label}
                                </a>
                            </Breadcrumb.Item>
                        ) : (
                            <Breadcrumb.Item key={idx}>{item.label}</Breadcrumb.Item>
                        )
                    )}
                </Breadcrumb>
                <Button
                    htmlType="button"
                    onClick={handleGoBack}
                    size="large"
                >
                    {t('common.goBack', { defaultValue: 'Go Back' })}
                </Button>
            </div>
            <Spin spinning={mutation.isPending || loadingClaim}>
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        isBabyBorn: false,
                    }}
                    onFinish={onFinish}
                    onFinishFailed={({ errorFields }) => {
                        form.scrollToField(errorFields[0].name, {
                            behavior: 'smooth',
                            block: 'center',
                        });
                    }} // Form submission handler
                    onValuesChange={handleValuesChange}
                >
                    <Card title={t("Claim Details")} style={{ marginBottom: 16 }}

                        extra={
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <strong>{t("New Baby Born")}</strong>

                                <Form.Item
                                    name="isBabyBorn"
                                    valuePropName="checked"
                                    noStyle
                                >
                                    <Switch size="small" />
                                </Form.Item>
                            </div>
                        }
                    >
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="policyHolderId"
                                    label={
                                            <span>
                                                {t("Card Number")}{" "}
                                                <Tooltip
                                                    title={
                                                        <>
                                                            <div>e.g.</div>
                                                            <div><strong>CM-0071-00011</strong></div>
                                                            <div><strong>IN-12345</strong></div>
                                                        </>
                                                    }
                                                >
                                                    <InfoCircleOutlined style={{ color: "#1890ff" }} />
                                                </Tooltip>
                                            </span>
                                    }

                                    rules={[{ required: true, message: t("Please enter policyHolderId") }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder={t("Type full card number to search")}
                                        size="large"
                                        filterOption={false} // custom search
                                        options={
                                            selectedPolicyHolder
                                                ? [selectedPolicyHolder, ...policyHolderOptions.filter(o => o.value !== selectedPolicyHolder.value)]
                                                : policyHolderOptions
                                        }
                                        value={form.getFieldValue('policyHolderId')}
                                        onSearch={async (input) => {
                                            const trimmed = input.trim().toUpperCase();

                                            const format1 = /^[A-Z]{2}-\d{4}-\d{5}$/;  // PK-2024-12345 (company's policy holder card number format)
                                            const format2 = /^[A-Z]{2}-\d{5}$/;        // IN-45877 (Individual's policy holder card number format)

                                            //  Only call API if format is correct
                                            if (format1.test(trimmed) || format2.test(trimmed)) {
                                                try {
                                                    const response = await get<{ data: PolicyHolderResponseItem[] }>(`${API_ENDPOINTS.CLAIM.GET_POLICY_HOLDERS_DROPDOWN}?cardNumber=${trimmed}&newisBabyBorn=${isBabyBorn}`);

                                                    const data = response.data;

                                                    // Separate errors from valid policy holders
                                                    const errorItem = data.find((d) => 'isError' in d && d.isError === 1);

                                                    if (errorItem) {
                                                        message.error(t(errorItem.message as string) || t("Card Number not found"));

                                                        // ✅ Clear previous form fields
                                                        form.setFieldsValue({
                                                            policyHolderId: undefined,
                                                            patientId: undefined,
                                                            bankDetailId: undefined,
                                                            services: [],
                                                            companyName: undefined,
                                                        });

                                                        setSelectedPolicyHolder(null);
                                                        setPolicyHolderOptions([]);
                                                        return;
                                                    }

                                                    const options: DropdownOption[] = data
                                                        .filter((p): p is PolicyHolder => p.isError === 0)
                                                        .map((p) => ({
                                                            label: p.cardNumber ? `${p.name} - ${p.cardNumber}` : p.name,
                                                            value: String(p.id),
                                                            policyHolderName: p.name,
                                                            ...p,
                                                            customerId: p.customerId ? Number(p.customerId) : undefined,
                                                        }));

                                                    setPolicyHolderOptions(options);

                                                    if (options.length === 1) {
                                                        // Auto-select if exactly one result
                                                        const selected = options[0];
                                                        form.setFieldsValue({ policyHolderId: selected.value, companyName: selected.companyName });
                                                        setSelectedPolicyHolder(selected);

                                                    }
                                                } catch (err) {
                                                    console.error(err);

                                                    // Also clear fields if API fails
                                                    form.setFieldsValue({
                                                        policyHolderId: undefined,
                                                        patientId: undefined,
                                                        bankDetailId: undefined,
                                                        services: [],
                                                        companyName: undefined,
                                                    });

                                                    setSelectedPolicyHolder(null);
                                                    setPolicyHolderOptions([]);
                                                }
                                            } else {

                                                //// Invalid format -> clear
                                                //form.setFieldsValue({
                                                //    policyHolderId: undefined,
                                                //    patientId: undefined,
                                                //    bankDetailId: undefined,
                                                //    services: [],
                                                //});

                                                //setSelectedPolicyHolder(null);
                                                //setPolicyHolderOptions([]); // clear if invalid

                                                // Invalid input: don't touch selected value, just optionally filter options
                                                const filtered = policyHolderOptions.filter(opt =>
                                                    opt.label.toUpperCase().includes(trimmed)
                                                );
                                                setPolicyHolderOptions(filtered);
                                            }
                                        }}
                                        onChange={(value, option) => {
                                            handlePolicyHolderChange(value);
                                            const opt = option as DropdownOption;
                                            setSelectedPolicyHolder(opt);
                                            form.setFieldsValue({ companyName: opt?.companyName || undefined });
                                        }}
                                    />
                                </Form.Item>
                                {/*    <Select*/}
                                {/*        showSearch*/}
                                {/*        placeholder={t("Select a policy holder")}*/}
                                {/*        loading={loadingPolicyHolders}*/}
                                {/*        options={policyHolders}*/}
                                {/*        optionFilterProp="label"*/}
                                {/*        size="large"*/}
                                {/*        onChange={handlePolicyHolderChange}*/}
                                {/*    />*/}
                                {/*</Form.Item>*/}
                                
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="patientId"
                                    label={t("Patient Name")}
                                    rules={[{ required: true, message: t("Select patient name") }]}
                                >
                                    <Select
                                        placeholder={t("Select patient")}
                                        loading={loadingPatients}
                                        options={patients}
                                        optionFilterProp="label"
                                        size="large"
                                    />

                                </Form.Item>
                            </Col>
                            
                            <Col span={8}>
                                <Form.Item name="companyName" label={t("Company")}>
                                    <Input disabled size="large" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="referenceNo" label={t("Reference No")}>
                                    <Input placeholder={t("Please enter a valid Reference No (25 letters)")} size="large" maxLength={25} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="claimDate"
                                    label={t("Claim Date")}
                                    rules={[
                                        { required: true, message: t("Please select a claim date") },
                                        () => ({
                                            validator(_, value) {
                                                const effectiveDate = selectedPolicyHolder?.effectiveDate || null;
                                                const expiryDate = selectedPolicyHolder?.expiryDate || null;

                                                if (!value) {
                                                    return Promise.reject(new Error(""));
                                                }

                                                if (expiryDate && (value.isAfter(expiryDate, "day") || value.isSame(expiryDate, "day"))) {
                                                    return Promise.reject(
                                                        new Error(t("Claim Date must be less or equal to expiry date"))
                                                    );
                                                }

                                                // validate only if claimDate exists
                                                if (effectiveDate && value.isBefore(effectiveDate, "day")) {
                                                    return Promise.reject(
                                                        new Error(t("Claim Date must be greater or equal to effective date"))
                                                    );
                                                }
                                                
                                                return Promise.resolve();
                                            },
                                        }),

                                        () => ({
                                            validator(_, value) {
                                                const today = dayjs();
                                                if (!value) {
                                                    return Promise.resolve();
                                                }
                                                // ❌ Must not be in the future
                                                if (dayjs(value).isAfter(today, "day")) {
                                                    return Promise.reject(
                                                        t("Enter a valid Claim Date that is not in the future.")
                                                    );
                                                }
                                                // ✅ Allow past and any previous date
                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    <DatePicker
                                        placeholder={t("Select claim date")}
                                        style={{ width: "100%" }}
                                        size="large"
                                        format="DD-MM-YYYY"
                                        // prevent user from selecting future dates in calendar
                                        disabledDate={(current) => current && current > dayjs().endOf("day")}
                                    />
                                </Form.Item>
                            </Col>


                            <Col span={8}>

                                <Form.Item
                                    name={isInNetwork ? "medicalProviderId" : "medicalProviderName"}
                                    label={
                                        <>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <span>{t("Medical Provider")}</span>
                                            </div>
                                            {userType != "medical" &&
                                                <div style={{ display: "flex", alignItems: "right", gap: "8px" }}>
                                                    <span style={{ fontSize: 12 }}>{isInNetwork ? t("(In Network)") : t("(Out of Network)")}</span>
                                                    <Switch checked={isInNetwork} onChange={handleSwitchChange} size="small" />
                                                </div>
                                            }
                                        </>
                                    }
                                    rules={isInNetwork
                                        ? [{ required: true, message: t("Please select medicalProviderId") }]
                                        : [
                                            { required: true, message: t("Please enter medical provider name") },
                                            { whitespace: true, message: t("Please enter medical provider name") },
                                        ]}
                                >
                                    {isInNetwork ? (
                                        <Select
                                            showSearch
                                            placeholder={t("Select a medical provider")}
                                            loading={loadingMedicalProviders}
                                            options={medicalProviders}
                                            optionFilterProp="label"
                                            size="large"
                                            onChange={handleProviderChange}
                                        />
                                    ) : (
                                        <Input
                                            placeholder={t("Enter medical provider name")}
                                            size="large"
                                        />
                                    )}
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item
                                    name="consultantName"
                                    label={t("Consultant Name")}
                                    rules={[
                                        {
                                            required: true, message: t("Mandatory")
                                        },
                                        {
                                            pattern: /^[A-Za-z\u0600-\u06FF .'-]+$/,
                                            message: t("Only English or Arabic letters, spaces, periods (.), hyphens (-), or apostrophes (') are allowed."),
                                        },
                                        {
                                            validator: (_, value) => {
                                                if (value) {
                                                    const trimmed = value.trim().replace(/\s+/g, " ");
                                                    if (trimmed.length < 1 || trimmed.length > 100) {
                                                        return Promise.reject(
                                                            t("Consultant name must be between 1 and 100 characters.")
                                                        );
                                                    }
                                                }
                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder={t("Enter consultant name")}
                                        size="large"
                                        onBlur={handleTrim}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item
                                    name="visitDate"
                                    label={t("Visit Date")}
                                    dependencies={["claimDate"]}
                                    rules={[
                                        {
                                            required: true, message: t("Mandatory")
                                        },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                const claimDate = getFieldValue("claimDate");

                                                if (!value) {
                                                    return Promise.reject(new Error(""));
                                                }

                                                // validate only if claimDate exists
                                                if (claimDate && value.isAfter(claimDate, "day")) {
                                                    return Promise.reject(
                                                        new Error(t("Visit Date cannot be after the Claim Date"))
                                                    );
                                                }

                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    <DatePicker
                                        style={{ width: "100%" }}
                                        size="large"
                                        placeholder={t("Select visit date")}
                                        format="DD-MM-YYYY"
                                        disabledDate={(current) =>
                                            current && current > dayjs().endOf("day")
                                        }
                                    />
                                </Form.Item>
                            </Col>


                        </Row>
                    </Card>

                    <Card title={t("Amount Details")} style={{ marginBottom: 16 }}>
                        <Form.List name="services">
                            {(fields, { remove }) => (
                                <>
                                    <div
                                        style={{
                                            background: token.colorBgContainer,
                                            borderRadius: 8,
                                            border: `1px solid ${token.colorBorder}`,
                                            overflow: 'hidden',
                                            boxShadow: token.boxShadowTertiary,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '44px 1fr 190px 48px',
                                                gap: 12,
                                                padding: '12px 14px',
                                                background: '#f6f7f9',
                                                borderBottom: `2px solid ${token.colorBorderSecondary}`,
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: token.colorTextHeading,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.4px',
                                            }}
                                        >
                                            <div>#</div>
                                            <div>{t('Sub Service')}</div>
                                            <div>{t('Amount')}</div>
                                            <div />
                                        </div>

                                        {fields.length === 0 ? (
                                            <div style={{ padding: '28px 14px', textAlign: 'center', color: token.colorTextSecondary }}>
                                                {t('No services added yet.')}
                                            </div>
                                        ) : (
                                            fields.map(({ key, name, ...restField }, index) => {
                                                const rowService = form.getFieldValue(['services', name]) || {};
                                                const isOtherService = String(rowService?.serviceCategoryItemId ?? '') === '0';

                                                return <div
                                                    key={key}
                                                    style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '44px 1fr 190px 48px',
                                                        gap: 12,
                                                        alignItems: 'center',
                                                        padding: '12px 14px',
                                                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                                                        borderInlineStart: isOtherService ? `3px solid ${token.colorWarning}` : undefined,
                                                        background: isOtherService
                                                            ? 'rgb(255, 230, 230)'
                                                            : index % 2 === 0
                                                                ? token.colorFillQuaternary
                                                                : token.colorBgContainer,
                                                    }}
                                                >
                                                    <div>
                                                        <span
                                                            style={{
                                                                display: 'inline-flex',
                                                                width: 28,
                                                                height: 28,
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                borderRadius: 8,
                                                                background: 'linear-gradient(135deg, #0f85ae 0%, #61aeb2 100%)',
                                                                color: '#ffffff',
                                                                fontWeight: 600,
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            {index + 1}
                                                        </span>
                                                    </div>

                                                    <Form.Item style={{ marginBottom: 0 }}>
                                                        {(() => {
                                                            const svc = rowService;
                                                            const svcName = svc?.serviceName;
                                                            const svcItemName = svc?.serviceCategoryItemName;
                                                            const svcParentName = svc?.serviceCategoryName;
                                                            const svcId = svc?.serviceCategoryItemId;
                                                            const svcLabelFromSelected = svc?.selected?.label;
                                                            let display = svcItemName || svcName || svcLabelFromSelected;
                                                            let catDisplay: string | undefined = svcParentName;
                                                            const idStr = svcId !== undefined && svcId !== null ? String(svcId) : undefined;
                                                            if (idStr && idStr !== '0') {
                                                                const flat = serviceItems.find((it: any) => String(it.value) === idStr);
                                                                if (flat && flat.label) {
                                                                    display = flat.label;
                                                                }

                                                                if (Array.isArray(serviceTreeData)) {
                                                                    for (const cat of serviceTreeData) {
                                                                        const child = (cat.children || []).find((c: any) => String(c.value) === idStr || String(c.key) === idStr);
                                                                        if (child) {
                                                                            catDisplay = cat.title || cat.label;
                                                                            break;
                                                                        }
                                                                    }
                                                                }
                                                                if (!catDisplay) {
                                                                    for (const cat of (serviceItems as any[])) {
                                                                        if (Array.isArray(cat.services)) {
                                                                            const s = cat.services.find((x: any) => String(x.serviceCategoryItemId ?? x.value) === idStr);
                                                                            if (s) {
                                                                                catDisplay = cat.serviceCategoryName || cat.label;
                                                                                break;
                                                                            }
                                                                        }
                                                                    }
                                                                }

                                                                if (!display && Array.isArray(serviceTreeData)) {
                                                                    for (const cat of serviceTreeData) {
                                                                        const child = (cat.children || []).find((c: any) => String(c.value) === idStr || String(c.key) === idStr);
                                                                        if (child) {
                                                                            display = child.title || child.label || String(idStr);
                                                                            break;
                                                                        }
                                                                    }
                                                                }

                                                                if (!display) {
                                                                    for (const cat of (serviceItems as any[])) {
                                                                        if (Array.isArray(cat.services)) {
                                                                            const s = cat.services.find((x: any) => String(x.serviceCategoryItemId ?? x.value) === idStr);
                                                                            if (s) {
                                                                                display = s.serviceCategoryItemName ?? s.label ?? String(idStr);
                                                                                break;
                                                                            }
                                                                        }
                                                                    }
                                                                }

                                                                if (!display) {
                                                                    console.warn('unable to resolve service name for id', idStr, { svc, serviceItems, serviceTreeData });
                                                                }

                                                                if (!display) display = idStr;
                                                            }
                                                            if (!display && idStr === '0') {
                                                                display = svcItemName || svcName || '';
                                                            }
                                                            if (catDisplay && display) {
                                                                display = `${catDisplay} > ${display}`;
                                                            }
                                                            return (
                                                                <Input
                                                                    disabled
                                                                    value={display}
                                                                    size='large'
                                                                    style={
                                                                        isOtherService
                                                                            ? {
                                                                                borderColor: token.colorWarningBorder,
                                                                                background: 'rgb(255, 230, 230)',
                                                                            }
                                                                            : undefined
                                                                    }
                                                                />
                                                            );
                                                        })()}
                                                    </Form.Item>

                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, 'amount']}
                                                        style={{ marginBottom: 0 }}
                                                        rules={[
                                                            amountValidator('Amount'),
                                                            currencyAmountValidator(selectedPolicyHolder?.currencyISOCode as string | undefined, ''),
                                                        ]}
                                                    >
                                                        <InputNumber style={{ width: '100%' }} size='large' min={1} max={100000000} />
                                                    </Form.Item>

                                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                        <Button
                                                            type='text'
                                                            icon={<DeleteOutlined style={{ color: token.colorError }} />}
                                                            onClick={() => remove(name)}
                                                            title={t('Delete')}
                                                            style={{
                                                                color: token.colorError,
                                                                borderRadius: 8,
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.background = token.colorErrorBg;
                                                                e.currentTarget.style.color = token.colorErrorHover;
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.background = 'transparent';
                                                                e.currentTarget.style.color = token.colorError;
                                                            }}
                                                        />
                                                    </div>
                                                </div>;
                                            })
                                        )}

                                        <div style={{ padding: 12, background: token.colorFillAlter }}>
                                            <div style={{ display: 'flex', gap: 8, flexWrap: 'nowrap' }}>
                                                <Button
                                                    id='serviceButton'
                                                    ghost
                                                    onClick={() => setIsAddServiceModalVisible(true)}
                                                    icon={<PlusOutlined />}
                                                    size='large'
                                                    disabled={allServicesAdded || loadingServiceItems}
                                                    style={{
                                                        flex: 1,
                                                        ...(allServicesAdded || loadingServiceItems
                                                            ? {
                                                                  color: token.colorTextDisabled,
                                                                  borderColor: token.colorBorder,
                                                                  background: token.colorFillTertiary,
                                                              }
                                                            : {}),
                                                    }}
                                                >
                                                    {t('Add Sub Service')}
                                                </Button>
                                                <Button
                                                    type='dashed'
                                                    onClick={() => setIsAddOtherServiceModalVisible(true)}
                                                    icon={<PlusOutlined />}
                                                    size='large'
                                                    disabled={loadingServiceItems || !policyHolderId || !planId}
                                                    style={{
                                                        flex: 1,
                                                        borderColor: token.colorErrorBorder,
                                                        color: token.colorError,
                                                        background: 'rgb(255, 230, 230)',
                                                        transition: 'all 0.2s ease',
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (e.currentTarget.hasAttribute('disabled')) return;
                                                        e.currentTarget.style.background = 'rgb(255, 215, 215)';
                                                        e.currentTarget.style.borderColor = token.colorError;
                                                        e.currentTarget.style.color = token.colorErrorHover;
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (e.currentTarget.hasAttribute('disabled')) return;
                                                        e.currentTarget.style.background = 'rgb(255, 230, 230)';
                                                        e.currentTarget.style.borderColor = token.colorErrorBorder;
                                                        e.currentTarget.style.color = token.colorError;
                                                    }}
                                                >
                                                    {t('Add Other Sub Service')}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </Form.List>
                        <Row style={{ marginTop: 16 }} >
                            <Col span={8}>
                                <Form.Item name="totalAmount" label={t("Total Amount")}>
                                    <InputNumber
                                        style={{ width: "100%" }}
                                        disabled
                                        placeholder={t("Calculated automatically")}
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <Card title={t("Payment Details")} style={{ marginBottom: 16 }}>
                        <Row gutter={16}>

                            <Col span={8}>
                                <Form.Item name="paymentMethod" label={t("Payment Method")} initialValue="Cash">
                                    <Radio.Group size="large">
                                        <Radio value="Cash">Cash</Radio>
                                        <Radio value="Bank">Bank</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                            {paymentMethod === "Bank" && (
                                <Col span={8}>
                                    <Form.Item
                                        name="bankDetailId"
                                        label={t("Bank Account")}
                                        rules={[{ required: true, message: t("Please select a bank account") }]}
                                    >
                                        <Select

                                            placeholder={t("Select bank account")}
                                            loading={loadingBankDetails}
                                            options={bankDetails}
                                            disabled={!policyHolderId}
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Button
                                        type="dashed"
                                        onClick={() => setIsBankModalVisible(true)}
                                        block
                                        icon={<PlusOutlined />}
                                        size="large"
                                        disabled={!policyHolderId}
                                        style={{ marginTop: 8 }}
                                    >
                                        {t("Manage Bank Accounts")}
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    </Card>

                    <Card title={t("Additional Information")} style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item name="comment" label={t("Comment")}>
                                    <Input.TextArea rows={4} placeholder={t("Enter comments")} size="large" maxLength={500} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <Card id="attachmentsCard" title={
                        <span>
                            {t("Attachments")}{" "}
                            <span style={{ color: "red" }}>*</span>
                        </span>
                    } style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    name="files"
                                    label={
                                        <span style={{ color: "red", fontWeight: 500 }}>
                                            {t("Note: Only .jpg, .jpeg, .png and .pdf formats are allowed.")}
                                        </span>
                                    }
                                >
                                    <FileUploadRequestField
                                        field={{
                                            name: "files",
                                            label: t("Upload Files"),
                                            type: "fileUpload",
                                            uploadUrl: API_ENDPOINTS.CLAIM.UPLOAD_FILES,
                                            maxCount: 5, // Allow multiple files, up to 5
                                            responseValueKey: "path", // Key in the response for the file path
                                        }}
                                        commonProps={{}}
                                        form={form}
                                        onUploadingChange={setIsFileUploading} // <-- here
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <Form.Item>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            <Button type="primary" htmlType="submit"
                                loading={mutation.isPending || isFileUploading}
                                disabled={mutation.isPending || isFileUploading}  // prevents double click
                                size="large">
                                {isEdit ? t("Update Claim") : t("Submit Claim")}
                            </Button>
                            <Button htmlType="button" onClick={handleGoBack} size="large">
                                {t("Cancel")}
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Spin>
            {selectedPolicyHolder && (
                <BankDetailsModal
                    visible={isBankModalVisible}
                    onClose={() => setIsBankModalVisible(false)}
                    policyHolder={selectedPolicyHolder}
                    onSuccess={handleBankModalSuccess}
                    cardNumber={selectedPolicyHolder.cardNumber} // Pass cardNumber here
                />
            )}

            <AddServiceItemModal
                medicalProviderId={medicalProviderId}
                isInNetwork={isInNetwork}
                planId={selectedPolicyHolder?.planId}
                policyHolderId={selectedPolicyHolder?.value as number | undefined}
                currencyISOCode={selectedPolicyHolder?.currencyISOCode as string | undefined}
                customerId={selectedPolicyHolder?.customerId}
                visible={isAddServiceModalVisible}
                onClose={() => setIsAddServiceModalVisible(false)}
                onAddService={handleAddService}
                existingServices={form.getFieldValue('services') || []}
            />

            <AddOtherServiceItemModal
                visible={isAddOtherServiceModalVisible}
                onClose={() => setIsAddOtherServiceModalVisible(false)}
                onAddService={handleAddService}
                existingServices={form.getFieldValue('services') || []}
                categories={serviceTreeData}
                currencyISOCode={selectedPolicyHolder?.currencyISOCode as string | undefined}
            />
        </>
    );
};

export default ClaimRequestsForm;